import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const token = params.get("token") || "";
  const missingToken = !token || token.length < 10;

  useEffect(() => {
    if (ok) {
      const t = setTimeout(() => nav("/login", { replace: true }), 3500);
      return () => clearTimeout(t);
    }
  }, [ok, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setOk(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24 pb-24 max-w-md mx-auto">
        <p className="eyebrow mb-4">Set new password</p>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-2">
          Choose a new password.
        </h1>
        <p className="text-zinc-400 mb-12 text-sm">Minimum 8 characters.</p>

        {missingToken ? (
          <div data-testid="reset-password-missing-token" className="border border-red-400/40 bg-red-400/[0.05] p-8">
            <p className="text-red-300">This reset link is missing or malformed.</p>
            <Link to="/forgot-password" className="text-white underline text-sm mt-3 inline-block">
              Request a new link
            </Link>
          </div>
        ) : ok ? (
          <div data-testid="reset-password-success-state" className="border border-[#E8A851]/40 bg-gradient-to-br from-[#E8A851]/10 to-transparent p-8">
            <CheckCircle2 className="w-10 h-10 text-[#E8A851] mb-4" />
            <p className="font-display text-2xl font-medium mb-3">Password updated.</p>
            <p className="text-zinc-300 text-sm">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-8 space-y-6">
            <div>
              <label className="eyebrow mb-2 block">New password</label>
              <input
                data-testid="reset-password-input"
                type="password"
                minLength={8}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-white text-white px-0 py-3 focus:outline-none"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Confirm</label>
              <input
                data-testid="reset-password-confirm-input"
                type="password"
                minLength={8}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-white text-white px-0 py-3 focus:outline-none"
              />
            </div>
            {err && <p data-testid="reset-password-error" className="text-sm text-red-400">{err}</p>}
            <button
              data-testid="reset-password-submit-button"
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-50"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <>Save new password</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
